import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import adminAuthService from '../services/authService';
import './AdminLogin.css';

const EmailIcon = () => (
  <svg className="al__input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const LockIcon = () => (
  <svg className="al__input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const EyeIcon = ({ visible }) => visible ? (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
) : (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const PasswordStrength = ({ password }) => {
  if (!password) return null;
  const getStrength = (pwd) => {
    let s = 0;
    if (pwd.length >= 8) s++; if (pwd.length >= 12) s++;
    if (/[A-Z]/.test(pwd)) s++; if (/[a-z]/.test(pwd)) s++;
    if (/[0-9]/.test(pwd)) s++; if (/[^A-Za-z0-9]/.test(pwd)) s++;
    if (s <= 2) return { score: 1, label: 'Weak', color: '#6b2d2d' };
    if (s <= 3) return { score: 2, label: 'Fair', color: '#8a7a3a' };
    if (s <= 4) return { score: 3, label: 'Good', color: '#3d5a3a' };
    return { score: 4, label: 'Strong', color: '#2a3f28' };
  };
  const strength = getStrength(password);
  return (
    <div className="al__strength">
      <div className="al__strength-bars">
        {[1, 2, 3, 4].map((l) => (
          <div key={l} className="al__strength-bar" style={{ backgroundColor: l <= strength.score ? strength.color : '#e8e8e8' }} />
        ))}
      </div>
      <span className="al__strength-label" style={{ color: strength.color }}>{strength.label}</span>
    </div>
  );
};

const PASSWORD_LOGIN_ENABLED = process.env.REACT_APP_ALLOW_PASSWORD_LOGIN !== 'false';

const AdminLogin = () => {
  const { login, verify2FA, isAuthenticated } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  const [view, setView] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [otpTimer, setOtpTimer] = useState(0);

  useEffect(() => { if (isAuthenticated) navigate(from, { replace: true }); }, [isAuthenticated, navigate, from]);
  useEffect(() => { if (otpTimer > 0) { const t = setTimeout(() => setOtpTimer(otpTimer - 1), 1000); return () => clearTimeout(t); } }, [otpTimer]);


  const switchView = (v) => { setView(v); setError(''); setSuccess(''); };

  // ─── Authenticator (2FA) ───
  const [tempToken, setTempToken] = useState('');
  const [code, setCode] = useState('');
  const [qr, setQr] = useState('');
  const [secret, setSecret] = useState('');
  const [fromSetup, setFromSetup] = useState(false);

  const startSetup = async (token) => {
    const r = await adminAuthService.setup2FA(token);
    setQr(r.qr); setSecret(r.secret); setCode(''); setFromSetup(true);
    switchView('setup');
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { setError('Enter the 6-digit code'); return; }
    setLoading(true); setError('');
    try {
      const r = await verify2FA(tempToken, code);
      if (r.success) navigate(from, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.message || 'Verification failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Please fill in all fields'); return; }
    setLoading(true); setError('');
    try {
      const r = await login(email, password);
      if (r.requires2FA) { setTempToken(r.tempToken); setCode(''); setFromSetup(false); switchView('twofa'); }
      else if (r.requiresSetup) { setTempToken(r.tempToken); await startSetup(r.tempToken); }
      else if (r.success && r.token) navigate(from, { replace: true });
    }
    catch (err) { setError(err?.response?.data?.message || 'Login failed.'); }
    finally { setLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) { setError('Please enter your email'); return; }
    setLoading(true); setError('');
    try { await adminAuthService.forgotPassword(email); setSuccess('If an account exists, you will receive an OTP.'); setView('otp'); setOtpTimer(60); }
    catch (err) { setError(err?.response?.data?.message || 'Failed to send OTP.'); }
    finally { setLoading(false); }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) { setError('Enter a valid 6-digit OTP'); return; }
    setLoading(true); setError('');
    try { const r = await adminAuthService.verifyOTP(email, otp); setResetToken(r.resetToken); setSuccess('OTP verified!'); setView('reset'); }
    catch (err) { setError(err?.response?.data?.message || 'Invalid OTP.'); }
    finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) { setError('Fill in all fields'); return; }
    if (newPassword.length < 8) { setError('Minimum 8 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true); setError('');
    try { await adminAuthService.resetPassword(resetToken, newPassword, confirmPassword); setSuccess('Password reset! You can now login.'); setView('login'); setPassword(''); setOtp(''); setNewPassword(''); setConfirmPassword(''); }
    catch (err) { setError(err?.response?.data?.message || 'Reset failed.'); }
    finally { setLoading(false); }
  };

  const handleResendOTP = async () => {
    if (otpTimer > 0) return;
    setLoading(true); setError('');
    try { await adminAuthService.forgotPassword(email); setSuccess('New OTP sent.'); setOtpTimer(60); }
    catch (err) { setError(err?.response?.data?.message || 'Failed.'); }
    finally { setLoading(false); }
  };

  const titles = { login: ['Welcome back', 'Sign in to your admin dashboard'], forgot: ['Forgot Password', "We'll send you a recovery code"], otp: ['Verify OTP', 'Check your inbox'], reset: ['New Password', 'Create a strong new password'], twofa: ['Verification', 'Enter your authenticator code'], setup: ['Set up Authenticator', 'Scan once, then enter the code'] };
  const [title, subtitle] = titles[view] || titles.login;

  return (
    <div className="al">
      <div className="al__container">
        {/* Branding */}
        <div className="al__branding">
          <div className="al__branding-inner">
            <h1 className="al__brand-name">Tripsie</h1>
            <p className="al__brand-desc">Admin Dashboard</p>
            <div className="al__brand-features">
              {['Manage trips & bookings', 'Real-time analytics', 'Secure admin access'].map((f, i) => (
                <div key={i} className="al__brand-feature">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
                  <span>{f}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="al__brand-deco">
            <div className="al__brand-circle" />
            <div className="al__brand-circle al__brand-circle--2" />
            <div className="al__brand-circle al__brand-circle--3" />
          </div>
        </div>

        {/* Mobile Header */}
        <div className="al__mobile-header">
          <h1 className="al__mobile-brand">Tripsie</h1>
          <p className="al__mobile-desc">Admin Dashboard</p>
        </div>

        {/* Form Panel */}
        <div className="al__panel">
          <div className="al__panel-inner">
            {/* Steps */}
            {!['login', 'twofa', 'setup'].includes(view) && (
              <div className="al__steps">
                {['Email', 'Verify', 'Reset'].map((label, i) => {
                  const idx = ['forgot', 'otp', 'reset'].indexOf(view);
                  const active = i <= idx;
                  return (
                    <React.Fragment key={label}>
                      {i > 0 && <div className={`al__step-line ${active ? 'active' : ''}`} />}
                      <div className={`al__step ${active ? 'active' : ''}`}>{i + 1}</div>
                    </React.Fragment>
                  );
                })}
              </div>
            )}

            <h2 className="al__title">{title}</h2>
            <p className="al__subtitle">{subtitle}</p>

            {error && <div className="al__alert al__alert--error"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg><span>{error}</span></div>}
            {success && <div className="al__alert al__alert--success"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg><span>{success}</span></div>}

            {/* Login */}
            {view === 'login' && PASSWORD_LOGIN_ENABLED && (
              <form onSubmit={handleLogin} className="al__form">
                <div className="al__field">
                  <label className="al__label">Email</label>
                  <div className="al__input-wrap"><EmailIcon />
                    <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setError(''); }} placeholder="admin@tripsie.com" className="al__input" autoComplete="email" disabled={loading} />
                  </div>
                </div>
                <div className="al__field">
                  <div className="al__label-row"><label className="al__label">Password</label>
                    <button type="button" className="al__forgot-btn" onClick={() => switchView('forgot')}>Forgot?</button>
                  </div>
                  <div className="al__input-wrap"><LockIcon />
                    <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => { setPassword(e.target.value); setError(''); }} placeholder="Enter your password" className="al__input" autoComplete="current-password" disabled={loading} />
                    <button type="button" className="al__eye-btn" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}><EyeIcon visible={showPassword} /></button>
                  </div>
                </div>
                <button type="submit" className="al__submit" disabled={loading}>{loading ? <span className="al__spinner" /> : 'Sign In'}</button>
              </form>
            )}

                        {/* 2FA first-time setup: QR page */}
            {view === 'setup' && (
              <div className="al__form">
                <p className="al__hint">1. Install <strong>Google Authenticator</strong> on your phone.<br />2. Tap <strong>+</strong> and scan this QR code.<br />3. Click <strong>Continue</strong> when it appears in the app.</p>
                {qr && <img src={qr} alt="Authenticator QR code" className="al__qr" />}
                <p className="al__hint">Can't scan? Enter this key manually:<br /><strong style={{ wordBreak: 'break-all' }}>{secret}</strong></p>
                <button type="button" className="al__submit" onClick={() => { setCode(''); switchView('twofa'); }}>Continue</button>
                <button type="button" className="al__back-btn" onClick={() => { setPassword(''); switchView('login'); }}>← Back to Sign In</button>
              </div>
            )}

            {/* 2FA code page */}
            {view === 'twofa' && (
              <form onSubmit={handleVerify2FA} className="al__form">
                <p className="al__hint">Open <strong>Google Authenticator</strong> and enter the 6-digit code shown for <strong>Tripsie Admin</strong>.</p>
                <div className="al__field"><label className="al__label">Authenticator Code</label>
                  <div className="al__input-wrap"><LockIcon /><input type="text" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }} placeholder="000000" className="al__input al__input--otp" maxLength={6} disabled={loading} autoFocus /></div>
                </div>
                <button type="submit" className="al__submit" disabled={loading || code.length !== 6}>{loading ? <span className="al__spinner" /> : 'Verify & Sign In'}</button>
                {fromSetup && (
                  <button type="button" className="al__back-btn" onClick={() => switchView('setup')}>← Back to QR code</button>
                )}
                <button type="button" className="al__back-btn" onClick={() => { setPassword(''); switchView('login'); }}>← Back to Sign In</button>
              </form>
            )}

            {/* Forgot */}
            {view === 'forgot' && (
              <form onSubmit={handleForgotPassword} className="al__form">
                <div className="al__field"><label className="al__label">Email Address</label>
                  <div className="al__input-wrap"><EmailIcon /><input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setError(''); }} placeholder="Enter your email" className="al__input" disabled={loading} /></div>
                </div>
                <button type="submit" className="al__submit" disabled={loading}>{loading ? <span className="al__spinner" /> : 'Send OTP'}</button>
                <button type="button" className="al__back-btn" onClick={() => switchView('login')}>← Back to Sign In</button>
              </form>
            )}

            {/* OTP */}
            {view === 'otp' && (
              <form onSubmit={handleVerifyOTP} className="al__form">
                <p className="al__hint">Enter the 6-digit code sent to <strong>{email}</strong></p>
                <div className="al__field"><label className="al__label">OTP Code</label>
                  <div className="al__input-wrap"><LockIcon /><input type="text" value={otp} onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }} placeholder="000000" className="al__input al__input--otp" maxLength={6} disabled={loading} /></div>
                </div>
                <button type="submit" className="al__submit" disabled={loading || otp.length !== 6}>{loading ? <span className="al__spinner" /> : 'Verify OTP'}</button>
                <div className="al__otp-actions">
                  <button type="button" className="al__resend-btn" onClick={handleResendOTP} disabled={otpTimer > 0 || loading}>{otpTimer > 0 ? `Resend in ${otpTimer}s` : 'Resend OTP'}</button>
                  <button type="button" className="al__back-btn" onClick={() => switchView('forgot')}>Change Email</button>
                </div>
              </form>
            )}

            {/* Reset */}
            {view === 'reset' && (
              <form onSubmit={handleResetPassword} className="al__form">
                <div className="al__field"><label className="al__label">New Password</label>
                  <div className="al__input-wrap"><LockIcon /><input type={showNewPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => { setNewPassword(e.target.value); setError(''); }} placeholder="Minimum 8 characters" className="al__input" disabled={loading} />
                    <button type="button" className="al__eye-btn" onClick={() => setShowNewPassword(!showNewPassword)} tabIndex={-1}><EyeIcon visible={showNewPassword} /></button></div>
                </div>
                <div className="al__field"><label className="al__label">Confirm Password</label>
                  <div className="al__input-wrap"><LockIcon /><input type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }} placeholder="Re-enter password" className="al__input" disabled={loading} />
                    <button type="button" className="al__eye-btn" onClick={() => setShowConfirmPassword(!showConfirmPassword)} tabIndex={-1}><EyeIcon visible={showConfirmPassword} /></button></div>
                </div>
                <PasswordStrength password={newPassword} />
                {confirmPassword && newPassword !== confirmPassword && <p className="al__match al__match--error">Passwords do not match</p>}
                {confirmPassword && newPassword === confirmPassword && confirmPassword.length >= 8 && <p className="al__match al__match--success">✓ Passwords match</p>}
                <button type="submit" className="al__submit" disabled={loading}>{loading ? <span className="al__spinner" /> : 'Reset Password'}</button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
